import type {TTiktokCountryScanSession} from "@/api/tiktokVisibilityApi";
import type {
  BuyoutBundleSerializer,
  CampaignApplicationAcceptedSerializer,
  CampaignManagementInfluencerSerializer,
  InvoiceSerializer,
  PendingAmbassadorSerializer,
  PotentialAmbassadorSerializer,
  PunishmentDetailSerializer,
  PunishmentSerializer,
} from "@/generated/DjangoTypes";
import type {TRecommendedPlans} from "@/store/campaignForm";
import type {TCarouselMedia} from "@/store/carousel";
import {INITIAL_TEMPLATE_STATE} from "@/store/constants";
import type {TFilters} from "@/store/filterStoreUtils";
import type {
  TAmbassadorshipEntryPoint,
  TBrandImpersonation,
  TCampaignType,
  TChatSortOption,
  TContentSubmission,
  TDisciplineCampaign,
  TDisciplineTab,
  TFloatingBuyouts,
  TInvoiceTab,
  TLanguage,
  TNormalInfluencerTab,
  TProInfluencerTab,
  TReferralTab,
  TSettingsModalState,
  TTemplateCustomContent,
  TTemplateForm,
} from "@/store/types";
import type {TCarouselUser} from "@/utils/campaignUtils";
import type {TAiContentApprovalMode} from "@/utils/contentApprovalMode";
import {atom} from "jotai";
import {atomWithImmer, withImmer} from "jotai-immer";
import {atomWithLocation} from "jotai-location";
import {atomWithReset, atomWithStorage} from "jotai/utils";
import * as RPNInput from "react-phone-number-input";

const INITIAL_TEMPLATE_CONTENT_STATE: TTemplateCustomContent = {
  feedback: "",
  subject: "",
  template_id: null,
  template_name: "",
};

type TContentReviewCustomContent = TTemplateCustomContent & {
  featured?: boolean | null;
  interests?: number[] | null;
  quality_score?: number | null;
};

export type TCreateCampaignFromGroup = {
  filterParams?: Partial<TFilters>;
  groupId: number;
  instagramCount?: number;
  tiktokCount?: number;
  ugcCount?: number;
};

export const acceptLanguageAtom = atom<string>("de");
export const ambassadorActionModalAtom = atom<{
  action?: "accept" | "decline";
  agencyId?: number;
  ambassador: PotentialAmbassadorSerializer | null;
  companyId?: number;
  entryPoint?: TAmbassadorshipEntryPoint;
  groupId?: number;
  influencerId?: number;
  isOpen: boolean;
  pendingAmbassador?: PendingAmbassadorSerializer | null;
}>({ambassador: null, isOpen: false});
export const ambassadorProgramModalAtom = withImmer(
  atom<{agencyId: number | null; companyId: number | null; isOpen: boolean}>({
    agencyId: null,
    companyId: null,
    isOpen: false,
  }),
);
export const ambassadorRequestModalAtom = atom<{
  companyId: number;
  entryPoint: TAmbassadorshipEntryPoint;
  influencer: {user: {first_name?: string}; uuid?: number};
} | null>(null);
export const inviteAmbassadorsModalAtom = withImmer(
  atom<{
    agencyId: number | null;
    companyId: number | null;
    isOpen: boolean;
    openedFromAmbassadorProgram: boolean;
    section: TReferralTab;
  }>({
    agencyId: null,
    companyId: null,
    isOpen: false,
    openedFromAmbassadorProgram: false,
    section: "public",
  }),
);
export const contentReviewCustomContentAtom = withImmer(
  atomWithStorage<TContentReviewCustomContent>("contentReview", {
    ...INITIAL_TEMPLATE_CONTENT_STATE,
    featured: null,
    interests: null,
    quality_score: null,
  }),
);
export const contentReviewTemplateFormAtom = withImmer(
  atomWithStorage<TTemplateForm>("adminFeedbackTemplate", INITIAL_TEMPLATE_STATE),
);
export const disciplinaryModalAtom = withImmer(
  atom<{
    campaignApplication?: TDisciplineCampaign;
    disciplineDuration?: number;
    influencer: {
      firstName?: string;
      id: number;
      image?: string | null;
      isInstagramVerified?: boolean;
      isTiktokVerified?: boolean;
      isUgcVerified?: boolean;
      username?: string | null;
    };
    instagramDiscipline?: PunishmentSerializer | null;
    isTimeToggleEnabled?: boolean;
    lastDiscipline?: PunishmentSerializer | null;
    selectedDiscipline?: PunishmentSerializer | PunishmentDetailSerializer | null;
    selectedUnverificationType?: TCampaignType;
    tiktokDiscipline?: PunishmentSerializer | null;
    ugcDiscipline?: PunishmentSerializer | null;
    userId?: number;
  }>({
    campaignApplication: undefined,
    disciplineDuration: 0,
    influencer: {
      id: 0,
      firstName: "",
      image: "",
      isInstagramVerified: false,
      isTiktokVerified: false,
      isUgcVerified: false,
      username: null,
    },
    instagramDiscipline: null,
    isTimeToggleEnabled: false,
    lastDiscipline: null,
    selectedDiscipline: null,
    selectedUnverificationType: undefined,
    tiktokDiscipline: null,
    ugcDiscipline: null,
    userId: undefined,
  }),
);
export const disciplineCustomContentAtom = withImmer(
  atomWithStorage<TTemplateCustomContent>("templateDisciplineContent", INITIAL_TEMPLATE_CONTENT_STATE),
);
export const disciplineTemplateFormAtom = withImmer(
  atomWithStorage<TTemplateForm>("templateDisciplineForm", {
    ...INITIAL_TEMPLATE_STATE,
    punishment_type: "PERMANENT_BAN",
  }),
);
export const locationAtom = atom({previousRoute: "", currentRoute: ""});
export const locationWithParamsAtom = atomWithLocation();
export const brandImpersonationAtom = atomWithStorage<TBrandImpersonation | null>("brandImpersonation", null);
export const languagePreferenceAtom = atomWithStorage<TLanguage | null>("languagePreference", null);
export const latestActiveCampaignForInviteAtom = atomWithStorage<number | null>("latestActiveCampaignForInvite", null);
export const influencerViewModeAtom = atomWithStorage<"grid" | "list">("influencerNetworkViewMode", "grid");
export const shouldMuteVideosAtom = atomWithStorage<boolean>("shouldMuteVideos", false);
export type ThemeMode = "light" | "dark" | "system";
export const themeModeAtom = atomWithStorage<ThemeMode>("theme", "system");
export const campaignIncidiesAtom = atomWithImmer<{
  active: number;
  complete: number;
  paused: number;
  planned: number;
  open: number;
}>({
  active: 1,
  complete: 1,
  paused: 1,
  planned: 1,
  open: 1,
});
export const campaignViewModeAtom = atomWithStorage<"grid" | "list">("campaignViewMode", "grid");
export const campaignDeleteAtom = atom<{id: number; draft?: boolean} | null | "ALL_DRAFTS">(null);

export const isFloatingBuyoutMenuVisibleAtom = atom<boolean>(false);
export const isDraggingSlider = atom<boolean>(false);
export const hasStartedCampaignCreationAtom = atom<boolean>(false);
export const groupDeleteAtom = atom<number | null>(null);
export const lastSelectedGroupIdAtom = atom<number | null>(null);
export const lastModifiedGroupInfluencerIdAtom = atom<number | null>(null);
export const showPartnershipAdsTutorialCtaAtom = atom<boolean>(true);
export const showSparkAdsTutorialCtaAtom = atom<boolean>(true);
export const scrollSectionAtom = atom<string | null>(null);
export const editedPaymentMethodIdAtom = atom<string | null>(null);
export const adminFeedbackLatestIndexAtom = atom<number>(0);
export const selectedCompanyIdAtom = atom<number | null>(null);

// Campaign fields atom with versioning system
export const campaignFieldsAtom = withImmer(
  atomWithStorage<{
    brand_does: {value: string; history: string[]; currentHistoryIndex: number};
    campaign_description: {value: string; history: string[]; currentHistoryIndex: number};
    task_description: {value: string; history: string[]; currentHistoryIndex: number};
  }>("campaignFields", {
    brand_does: {value: "", history: [], currentHistoryIndex: -1},
    campaign_description: {value: "", history: [], currentHistoryIndex: -1},
    task_description: {value: "", history: [], currentHistoryIndex: -1},
  }),
);
export const blockInfluencerModalAtom = atom<{
  campaignApplicationId?: number;
  companyId: number;
  influencerId: number;
} | null>(null);
export const campaignOverviewOpenAtom = withImmer(
  atom<{height: number; isOpen: boolean; isSticky: boolean; isStuck: boolean}>({
    height: 0,
    isOpen: true,
    isSticky: true,
    isStuck: false,
  }),
);
export const cancelApplicationModalDataAtom = atom<{
  application: {
    can_be_cancelled_directly?: boolean;
    id: number;
    payment_source?: CampaignApplicationAcceptedSerializer["payment_source"];
    product_sent?: boolean;
    refund_amount?: number;
    voucher?: string | null;
  };
  campaign: {
    id: number;
    service_is_physical_product?: boolean;
    service_is_voucher?: boolean;
    title?: string | null;
    ugc?: boolean;
  };
  influencer: {first_name?: string | null; picture?: string | null; picture320?: string | null};
} | null>(null);
export const chatDraftMessagesAtom = withImmer(atom<Record<string, string>>({}));
export const chatSortAtom = atom<TChatSortOption>("needs_reply");
export const confirmCampaignEndingModalAtom = atom<{
  campaignId: number | null;
  companyId: number | null;
  isOpen: boolean;
}>({
  campaignId: null,
  companyId: null,
  isOpen: false,
});
export const createCampaignForCompanyAtom = atom<number | null>(null);
export const createCampaignFromGroupAtom = atom<TCreateCampaignFromGroup | null>(null);
export const createCustomerModalOpenAtom = atom<boolean>(false);
export const customerSuccessModalAgencyIdAtom = atom<number | null>(null);
export const declineCancellationRequestModalDataAtom = atom<{campaignId: number; requestId: number} | null>(null);
export const deleteFilterModalAtom = atom<{filterId: number; onSuccess?: () => void} | null>(null);
export const discardFilterChangesModalAtom = atom<{
  action: () => void;
  filterId: number;
  filterName: string;
  filterParams: TFilters;
} | null>(null);
export const discardSettingsChangesModalAtom = atom<{target: TSettingsModalState | null} | null>(null);
export const editFilterAtom = atom<{
  filterId: number;
  filterName: string;
  snapshotFilters: TFilters;
  snapshotName: string;
} | null>(null);
export const disciplineTabAtom = atom<TDisciplineTab>("UNVERIFY");
export const extendCampaignModalDataAtom = atom<{
  application?: {completed?: string | null; id: number; first_name?: string | null};
  campaign?: {
    completed?: string | null;
    id: number;
    is_gathering_reporting?: boolean;
    on_going?: boolean;
    preparation?: string | null;
    ugc?: boolean;
  };
} | null>(null);
export const hasClosedPaymentFailedModalAtom = atomWithStorage<boolean>("hasClosedPaymentFailedModal", false);
export const hasJustRegisteredAtom = atom<boolean>(false);
export const hidePublishShareNudgeAtom = atomWithStorage<Record<number, boolean>>(
  "hidePublishShareNudgeByUser",
  {},
  undefined,
  {getOnInit: true},
);
export const inviteWrongAccountModalAtom = atom<{inviteEmail: string; inviteUrl: string} | null>(null);
export const invoiceTabStateAtom = atom<TInvoiceTab>("campaigns_and_plans");
export const isAddTiktokToPlanModalOpenAtom = atom<boolean>(false);
export const isAdminFeedbackModalOpenAtom = atom<boolean>(false);
export const isAgencyInviteModalOpenAtom = atom<boolean>(false);
export const isChatMessagePendingAtom = atom(false);
export const isCommunityCodesCsvModalOpenAtom = atom<boolean>(false);
export const isConfirmEmailModalOpenAtom = atom<boolean>(false);
export const isDeleteTemplateModalOpenAtom = atom(false);
export const isDisciplinaryModalOpenAtom = atom(false);
export const isEditProfileModalOpenAtom = atom(false);
export const isFeedbackModalOpenAtom = atom<boolean>(false);
export const isFinishRegistrationModalVisibleAtom = atom<boolean>(false);
const isMoreBuyoutsModalOpenPrimitiveAtom = atomWithReset<{
  show: boolean;
  type: BuyoutBundleSerializer["bundle_type"];
}>({show: false, type: "EXTRA_BUYOUTS"});
export const isInfluencerSidebarOpenAtom = atom(false);
export const isIntercomBootedAtom = atom(false);
export const isMainSidebarCollapsedAtom = atomWithStorage("sidebarCollapsed", false);
export const isManageProductLinesModalOpenAtom = atom(false);
export const isMobileSidebarOpenAtom = atom(false);
export const isInvalidCompanyUrlModalOpenAtom = atom(false);
export const isMoreBuyoutsModalOpenAtom = withImmer(isMoreBuyoutsModalOpenPrimitiveAtom);
export const isMoreInviteModalOpenAtom = atom<boolean>(false);
export const isPurchasePlanModalOpenAtom = atom<boolean>(false);
export const isRateInfluencersModalOpenAtom = atom(false);
export const isRegisterCompanyMemberModalOpenAtom = atom<boolean>(false);
const isRequestMoreResourcesModalOpenPrimitveAtom = atomWithReset<{
  show: boolean;
  type: TFloatingBuyouts;
}>({show: false, type: "buyouts"});
export const isRequestMoreResourcesModalOpenAtom = withImmer(isRequestMoreResourcesModalOpenPrimitveAtom);
export const isRequestPermissionsModalOpenAtom = atom<boolean>(false);
export const isSidebarProfileMenuOpenAtom = atom<boolean>(false);
export const revertFromAiContentApprovalModalAtom = atom<"manual" | "off" | null>(null);
export const setAiAutonomyModalAtom = atom<TAiContentApprovalMode | null>(null);
export const isSingleDisciplineModalOpenAtom = atom(false);
export const isTiktokUpgradePendingAtom = atom<boolean>(false);
export const switchToAiContentApprovalModalAtom = atom<TAiContentApprovalMode | null>(null);
export const isTriggeredFromDisciplinaryModalAtom = atom(false);
export const isWelcomeToMultiAccountModalOpenAtom = atom<boolean>(false);
export const multiAccountInviteModalAtom = atom<{
  isVisible: boolean;
  isViewingOwnCompany: boolean;
}>({
  isVisible: false,
  isViewingOwnCompany: false,
});
export const normalInfluencerTabAtom = atom<TNormalInfluencerTab>("open_applications");
export const openTopBarPopoverAtom = atom<"language" | "theme" | null>(null);
export const paymentFailedModalDataAtom = atom<InvoiceSerializer[] | null>(null);
export const pendingOptimisticChatMessageIdsAtom = atom<Set<number>>(new Set<number>());
export const publishShareNudgeModalAtom = withImmer(
  atom<{
    companyId: number | null;
    isOpen: boolean;
  }>({
    companyId: null,
    isOpen: false,
  }),
);
export const purchaseModalContentAtom = atom<{
  buyoutsPerContent: number;
  contents: TCarouselMedia[];
  onPurchased?: () => void;
}>({buyoutsPerContent: 1, contents: []});
export const proInfluencerTabAtom = atom<TProInfluencerTab>("open_applications_pro");
export const recommendedPlansAtom = atom<TRecommendedPlans>();
export const refundInfluencerModalDataAtom = atom<{
  amount: number;
  applicationId: number | null;
  isOpen: boolean;
  payment_source?: CampaignApplicationAcceptedSerializer["payment_source"];
}>({
  amount: 0,
  applicationId: null,
  isOpen: false,
});
export const overrideSubmissionModalAtom = atom<number | null>(null);
export const rejectContentModalAtom = withImmer(
  atom<{
    isOpen: boolean;
    submission: TContentSubmission | null;
    submissionId: number | null;
    user: TCarouselUser | null;
  }>({
    isOpen: false,
    submission: null,
    submissionId: null,
    user: null,
  }),
);
export const requestTakedownModalAtom = atom<number | null>(null);
export const reviewContentModalAtom = withImmer(
  atom<{
    isOpen: boolean;
    isRevisionFormOpen: boolean;
    showRevisionFormClosingButton: boolean;
    submissionId: number | null;
  }>({
    isOpen: false,
    isRevisionFormOpen: false,
    showRevisionFormClosingButton: true,
    submissionId: null,
  }),
);
export const revisionAtom = withImmer(atom<Record<number, {caption?: string; feedback?: string}>>({}));
export const sendProductModalDataAtom = atom<{applicationId: number; trackingUrl?: string} | null>(null);
export const settingsDirtyAtom = atom(false);
export const settingsModalAtom = atom<TSettingsModalState | null>(null);
export const showProInfluencersContentBuyoutInfoAtom = atomWithStorage<boolean>(
  "showProInfluencersContentBuyoutInfo",
  true,
);
export const submissionsDataAtom = atom<{
  influencer: CampaignManagementInfluencerSerializer | null;
  submissions?: TCarouselMedia[];
}>({influencer: null});
export const taskCompletedOnAtom = atom<string | null | undefined>();
export const templateLanguageAtom = atom<TLanguage>("en_EN");
export const tiktokCountryAddSessionAtom = atom<TTiktokCountryScanSession | null>(null);
export const tiktokCountrySwitchSessionAtom = atom<TTiktokCountryScanSession | null>(null);
export const tiktokVisibilitySelectedCountryAtom = atomWithStorage<Record<number, RPNInput.Country>>(
  "tiktokVisibilitySelectedCountryByCompany",
  {},
);
export const manageCreditsModalAtom = withImmer(
  atom<{
    credits?: number | null;
    influencerFees?: {pro?: number | null; regular?: number | null};
    isOpen: boolean;
    onConfirm?: (newBalance: number) => void;
    type: "add" | "remove";
  }>({
    isOpen: false,
    type: "add",
  }),
);
